import React, { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, RefreshCw, Eye, EyeOff } from "lucide-react";
import ModalOverlay from "../UI/ModalOverlay/ModalOverlay.tsx";
import {
  createJobboardPreRegister,
  createStandardPreRegister,
  completeJobboardRegistration,
  completeJobboardNewUserRegistration,
} from "../../services/jobboardRegistrationService.ts";
import {
  validateEmail,
  parsePreRegisterError,
  validatePassword,
  validateConfirmPassword,
  isJobBoardLegacyUserNotFoundError,
  parseJobboardPreRegisterSuccessResponse,
} from "../../utils/registration/registrationHelpers.ts";
import { PASSWORD_VALIDATION } from "../../utils/registration/registrationConstants.ts";
import PDF from "../../components/Footer/PDF/Terminos-y-condiciones-edify-2021.pdf";
import { useJobboardCodeValidation } from "../../hooks/jobboard/useJobboardCodeValidation.ts";
import { fireAnalytics } from "../../utils/firebase";
import { getSessionId, getDeviceCategory } from "../../utils/analyticsHelpers.ts";
import { setLoginAnimation, setLoginAnimationType } from "../../store/actions/authActions";
import { useDispatch } from "react-redux";
import {
  establishSessionAfterMigracionBolsa,
  establishSessionAfterAltaNuevaBolsa,
} from "../../utils/jobboard/establishSessionAfterJobboardRegister.ts";
import { userNeedsJobBoardProfileCompletion } from "../../utils/jobboard/jobboardProfileGate.ts";
import styles from "./JobboardRegistrationFlow.module.scss";

export type JobboardRegistrationStep = "email" | "code" | "password" | "existing-login";
type Step = JobboardRegistrationStep;
type PreRegisterStatus = "ALREADY_REGISTERED" | "ALREADY_VALIDATED";
type LoginRedirectVariant = "registered" | "validated" | "duplicate";
type RegistrationCompletePath = "legacy-migrate" | "standard-new";
export type JobboardRegistrationEntry = "header" | "apply";

interface JobboardRegistrationFlowProps {
  isOpen?: boolean;
  onClose: () => void;
  workOffer?: { id?: string | number } | null;
  isExternalOffer?: boolean;
  registrationEntry?: JobboardRegistrationEntry;
  onSuccess?: (meta?: { flow: "new-registration" | "returning-login" | "needs-jobboard-onboarding" }) => void;
  embedded?: boolean;
  initialEmail?: string;
  onStepChange?: (step: JobboardRegistrationStep) => void;
}

const LOGIN_REDIRECT_HEADLINES: Record<LoginRedirectVariant, string> = {
  registered: "Este email ya está registrado.",
  validated: "Ya validamos este email.",
  duplicate: "Este email ya tiene cuenta.",
};

const JobboardRegistrationFlow: React.FC<JobboardRegistrationFlowProps> = ({
  isOpen = true,
  onClose,
  workOffer,
  isExternalOffer = false,
  onSuccess,
  registrationEntry = "header",
  embedded = false,
  initialEmail = "",
  onStepChange,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [step, setStep] = useState<Step>("email");

  useEffect(() => {
    onStepChange?.(step);
  }, [step, onStepChange]);
  const [email, setEmail] = useState(initialEmail);
  const [emailError, setEmailError] = useState("");

  useEffect(() => {
    if (initialEmail) setEmail(initialEmail);
  }, [initialEmail]);
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);
  const [existingLoginPassword, setExistingLoginPassword] = useState("");
  const [existingLoginError, setExistingLoginError] = useState("");
  const [isSubmittingExistingLogin, setIsSubmittingExistingLogin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showExistingLoginPassword, setShowExistingLoginPassword] = useState(false);
  const [alreadyRegisteredMessage, setAlreadyRegisteredMessage] = useState("");
  const [loginRedirectPath, setLoginRedirectPath] = useState("/");
  const [loginCountdown, setLoginCountdown] = useState<number | null>(null);
  const [registrationCompletePath, setRegistrationCompletePath] =
    useState<RegistrationCompletePath>("legacy-migrate");
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const redirectToLoginWithMessage = useCallback((variant: LoginRedirectVariant) => {
    setAlreadyRegisteredMessage(LOGIN_REDIRECT_HEADLINES[variant]);
    setLoginRedirectPath("/");
    setLoginCountdown(12);
    setEmailError("");
  }, []);

  useEffect(() => {
    if (loginCountdown === null) return;
    if (loginCountdown <= 0) {
      onClose();
      navigate(loginRedirectPath);
      return;
    }
    const timer = window.setTimeout(() => {
      setLoginCountdown((prev) => (prev === null ? null : prev - 1));
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [loginCountdown, loginRedirectPath, navigate, onClose]);

  const handleLoginNow = useCallback(() => {
    setLoginCountdown(null);
    onClose();
    navigate(loginRedirectPath);
  }, [loginRedirectPath, navigate, onClose]);

  const handleEmailSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const validation = validateEmail(email);
      if (!validation.isValid) {
        setEmailError(validation.error || "");
        return;
      }
      setEmailError("");
      setAlreadyRegisteredMessage("");
      setLoginCountdown(null);
      setRegistrationCompletePath("legacy-migrate");
      setIsSubmittingEmail(true);
      try {
        const response = await createJobboardPreRegister(email.trim().toLowerCase());
        const parsed = parseJobboardPreRegisterSuccessResponse(response);
        const status = parsed.status as PreRegisterStatus | undefined;

        if (status === "ALREADY_REGISTERED" || status === "ALREADY_VALIDATED") {
          if (parsed.role === "JOBBOARD") {
            const resolvedEmail = (parsed.emailFromApi ?? email).trim().toLowerCase();
            setEmail(resolvedEmail);
            setLoginCountdown(null);
            setAlreadyRegisteredMessage("");
            setExistingLoginPassword("");
            setExistingLoginError("");
            setStep("existing-login");
            return;
          }
          redirectToLoginWithMessage(status === "ALREADY_VALIDATED" ? "validated" : "registered");
          return;
        }

        setRegistrationCompletePath("legacy-migrate");
        setStep("code");
        fireAnalytics("registro_email_enviado", { category: "usuario" });
      } catch (error: any) {
        if (isJobBoardLegacyUserNotFoundError(error)) {
          try {
            await createStandardPreRegister(email.trim().toLowerCase());
            setRegistrationCompletePath("standard-new");
            setStep("code");
            setEmailError("");
            fireAnalytics("registro_email_enviado", {
              category: "usuario",
              jobboard_flow: "standard_new",
            });
          } catch (inner: any) {
            const parsed = parsePreRegisterError(inner);
            if (parsed.shouldRedirectToLogin) {
              redirectToLoginWithMessage("duplicate");
            } else if (parsed.isPreRegistered) {
              setRegistrationCompletePath("standard-new");
              if (parsed.isAlreadyValidated) {
                setStep("password");
              } else {
                setStep("code");
              }
              setEmailError("");
            } else {
              setEmailError(parsed.message);
            }
          }
        } else {
          const parsed = parsePreRegisterError(error);
          if (parsed.shouldRedirectToLogin) {
            redirectToLoginWithMessage("duplicate");
          } else if (parsed.isPreRegistered) {
            if (parsed.isAlreadyValidated) {
              setStep("password");
            } else {
              setStep("code");
            }
            setEmailError("");
          } else {
            setEmailError(parsed.message);
          }
        }
      } finally {
        setIsSubmittingEmail(false);
      }
    },
    [email, redirectToLoginWithMessage]
  );

  const handleCodeSuccess = useCallback(() => {
    setStep("password");
  }, []);

  const {
    code,
    setCode,
    codeError,
    countDown,
    showResend,
    handleResend,
    handleChangeEmail,
    handleCodeChange,
    handlePaste,
    isLoading: isCodeLoading,
  } = useJobboardCodeValidation({
    email,
    onSuccess: handleCodeSuccess,
    onChangeEmail: () => {
      setStep("email");
      setRegistrationCompletePath("legacy-migrate");
    },
  });

  const onCodeInputChange = (index: number, value: string) => {
    handleCodeChange(index, value);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePasswordSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const pv = validatePassword(password);
      const cv = validateConfirmPassword(password, confirmPassword);
      if (!pv.isValid) {
        setPasswordError(pv.error || "");
        return;
      }
      if (!cv.isValid) {
        setConfirmPasswordError(cv.error || "");
        return;
      }
      if (!acceptTerms) return;
      setPasswordError("");
      setConfirmPasswordError("");
      setIsSubmittingPassword(true);
      try {
        const completeRegistration =
          registrationCompletePath === "standard-new"
            ? completeJobboardNewUserRegistration
            : completeJobboardRegistration;
        await completeRegistration(email.trim().toLowerCase(), password);
        fireAnalytics("registro_completado_exitoso", {
          category: "onboarding",
          session_id: getSessionId(),
          device_category: getDeviceCategory(),
          action: "registration_completed",
          jobboard_complete_path: registrationCompletePath,
          jobboard_registration_entry: registrationEntry,
        });
        dispatch(setLoginAnimationType("register"));
        dispatch(setLoginAnimation(true));
        try {
          let obtainedUserData:
            | {
                rols?: { name?: string }[];
                type?: string | null;
              }
            | undefined;
          try {
            const establishSession =
              registrationCompletePath === "standard-new"
                ? establishSessionAfterAltaNuevaBolsa
                : establishSessionAfterMigracionBolsa;
            const data = await establishSession(email.trim().toLowerCase(), password, dispatch);
            obtainedUserData = data;
          } catch {
            //
          }
          const shouldGoToOnboarding =
            registrationEntry === "header" &&
            obtainedUserData &&
            userNeedsJobBoardProfileCompletion(obtainedUserData);

          if (onSuccess) {
            if (shouldGoToOnboarding) {
              onSuccess({ flow: "needs-jobboard-onboarding" });
            } else {
              onSuccess({ flow: "new-registration" });
            }
          } else {
            onClose();
            const destination = shouldGoToOnboarding
              ? "/bolsadetrabajo/completar-perfil"
              : registrationCompletePath === "standard-new"
                ? "/bolsadetrabajo/completar-perfil"
                : "/bolsadetrabajo/registro-exitoso";
            navigate(destination);
          }
        } finally {
          dispatch(setLoginAnimation(false));
          dispatch(setLoginAnimationType("login"));
        }
      } catch (error: any) {
        setPasswordError(
          error?.response?.data?.message || "Error al completar el registro. Intenta nuevamente."
        );
      } finally {
        setIsSubmittingPassword(false);
      }
    },
    [
      email,
      password,
      confirmPassword,
      acceptTerms,
      registrationCompletePath,
      registrationEntry,
      onSuccess,
      onClose,
      navigate,
      dispatch,
    ]
  );

  const handleExistingLoginSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!existingLoginPassword) {
        setExistingLoginError("Ingresá tu contraseña");
        return;
      }
      setExistingLoginError("");
      setIsSubmittingExistingLogin(true);
      dispatch(setLoginAnimationType("login"));
      dispatch(setLoginAnimation(true));
      try {
        const obtainedUserData = await establishSessionAfterAltaNuevaBolsa(
          email.trim().toLowerCase(),
          existingLoginPassword,
          dispatch
        );
        fireAnalytics("registro_completado_exitoso", {
          category: "onboarding",
          session_id: getSessionId(),
          device_category: getDeviceCategory(),
          action: "jobboard_returning_login",
          jobboard_registration_entry: registrationEntry,
        });
        const shouldGoToOnboarding =
          registrationEntry === "header" &&
          userNeedsJobBoardProfileCompletion(obtainedUserData);

        if (onSuccess) {
          if (shouldGoToOnboarding) {
            onSuccess({ flow: "needs-jobboard-onboarding" });
          } else {
            onSuccess({ flow: "returning-login" });
          }
        } else {
          onClose();
          navigate(shouldGoToOnboarding ? "/bolsadetrabajo/completar-perfil" : "/bolsadetrabajo");
        }
      } catch (error: any) {
        setExistingLoginError(
          error?.response?.data?.message || "No pudimos iniciar sesión. Revisá tu contraseña."
        );
      } finally {
        dispatch(setLoginAnimation(false));
        dispatch(setLoginAnimationType("login"));
        setIsSubmittingExistingLogin(false);
      }
    },
    [
      dispatch,
      email,
      existingLoginPassword,
      navigate,
      onClose,
      onSuccess,
      registrationEntry,
    ]
  );

  const handleClose = useCallback(() => {
    setStep("email");
    setEmail("");
    setEmailError("");
    setAlreadyRegisteredMessage("");
    setLoginRedirectPath("/");
    setLoginCountdown(null);
    setRegistrationCompletePath("legacy-migrate");
    setCode("");
    setPassword("");
    setConfirmPassword("");
    setAcceptTerms(false);
    setExistingLoginPassword("");
    setExistingLoginError("");
    setIsSubmittingExistingLogin(false);
    setShowExistingLoginPassword(false);
    onClose();
  }, [onClose, setCode]);

  if (!embedded && !isOpen) return null;

  const content = (
    <div className={embedded ? styles.embedded : styles.modal} onClick={embedded ? (e) => e.stopPropagation() : undefined}>
      {!embedded && (
        <header className={styles.header}>
          <h2 className={styles.title}>
            {step === "email" && "Inscribite para aplicar"}
            {step === "code" && "Verificá tu email"}
            {step === "password" && "Creá tu contraseña"}
            {step === "existing-login" && "Iniciá sesión"}
          </h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleClose}
            aria-label="Cerrar"
          >
              <X size={20} />
          </button>
        </header>
      )}

      <div className={styles.body}>
          {step === "email" && (
            <form onSubmit={handleEmailSubmit} className={styles.form}>
              {alreadyRegisteredMessage ? (
                <>
                  <p className={styles.loginStateLead}>{alreadyRegisteredMessage}</p>
                  <p className={styles.nextStepsIntro}>Para postularte:</p>
                  <ol className={styles.nextStepsList}>
                    <li>Iniciá sesión.</li>
                    <li>
                      Andá a <span className={styles.menuPath}>Explorar</span>
                      {" · "}
                      <span className={styles.menuPath}>Postularme a ofertas de trabajo</span>.
                    </li>
                    <li>Ahí están todas las ofertas.</li>
                  </ol>
                  {loginCountdown !== null && (
                    <p className={styles.redirectInfo} aria-live="polite">
                      Te llevamos al inicio en <span className={styles.redirectSeconds}>{loginCountdown}</span> s
                    </p>
                  )}
                  <button type="button" className={styles.primaryButton} onClick={handleLoginNow}>
                    Ir al inicio de sesión
                  </button>
                </>
              ) : (
                <>
                  <p className={styles.subtitle}>
                    Ingresá tu email para registrarte en la bolsa de trabajo y postularte a las
                    ofertas.
                  </p>
                  <input
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError("");
                    }}
                    className={styles.input}
                    disabled={isSubmittingEmail}
                    autoComplete="email"
                  />
                  {emailError && <p className={styles.error}>{emailError}</p>}
                  <button
                    type="submit"
                    className={styles.primaryButton}
                    disabled={isSubmittingEmail || !email.trim()}
                  >
                    {isSubmittingEmail ? "Enviando..." : "Continuar"}
                  </button>
                </>
              )}
            </form>
          )}

          {step === "code" && (
            <div className={styles.codeStep}>
              <p className={styles.contextText}>
                Te enviamos un código de verificación a tu email para confirmar que es tuyo. Ingresalo abajo para continuar con tu registro en la bolsa de trabajo.
              </p>
              <p className={styles.subtitle}>
                Código enviado a <strong>{email}</strong>
              </p>
              <div className={styles.codeContainer}>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <input
                    key={i}
                    ref={(el) => (inputRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={code[i] || ""}
                    onChange={(e) => onCodeInputChange(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    onPaste={handlePaste}
                    className={styles.codeInput}
                    disabled={isCodeLoading}
                    placeholder="-"
                  />
                ))}
              </div>
              {codeError && <p className={styles.error}>{codeError}</p>}
              <div className={styles.codeActions}>
                {showResend ? (
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={isCodeLoading}
                    className={styles.resendButton}
                  >
                    <RefreshCw size={18} />
                    {isCodeLoading ? "Reenviando..." : "Reenviar código"}
                  </button>
                ) : (
                  <p className={styles.countdown}>
                    <RefreshCw size={18} />
                    Reenviar en {countDown}s
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={handleChangeEmail}
                className={styles.backLink}
              >
                Cambiar email
              </button>
            </div>
          )}

          {step === "existing-login" && (
            <form onSubmit={handleExistingLoginSubmit} className={styles.form}>
              <p className={styles.contextText}>
                Ya tenés cuenta en la bolsa de trabajo. Iniciá sesión con tu contraseña para continuar.
              </p>
              <p className={styles.subtitle}>
                Correo: <strong>{email}</strong>
              </p>
              <div className={styles.passwordGroup}>
                <input
                  type={showExistingLoginPassword ? "text" : "password"}
                  placeholder="Contraseña"
                  value={existingLoginPassword}
                  onChange={(e) => {
                    setExistingLoginPassword(e.target.value);
                    setExistingLoginError("");
                  }}
                  className={styles.input}
                  disabled={isSubmittingExistingLogin}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowExistingLoginPassword(!showExistingLoginPassword)}
                  className={styles.eyeButton}
                  aria-label={showExistingLoginPassword ? "Ocultar" : "Mostrar"}
                >
                  {showExistingLoginPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {existingLoginError && <p className={styles.error}>{existingLoginError}</p>}
              <button
                type="submit"
                className={styles.primaryButton}
                disabled={isSubmittingExistingLogin || !existingLoginPassword}
              >
                {isSubmittingExistingLogin ? "Ingresando..." : "Ingresar"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setStep("email");
                  setExistingLoginPassword("");
                  setExistingLoginError("");
                }}
                className={styles.backLink}
              >
                Usar otro email
              </button>
            </form>
          )}

          {step === "password" && (
            <form onSubmit={handlePasswordSubmit} className={styles.form}>
              <p className={styles.contextText}>
                Elegí una contraseña segura para tu cuenta. Con esto vas a poder acceder a la bolsa de trabajo y postularte a las ofertas.
              </p>
              <p className={styles.subtitle}>Contraseña para {email}</p>
              <div className={styles.passwordGroup}>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Ingresá tu contraseña"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setPasswordError("");
                  }}
                  className={styles.input}
                  disabled={isSubmittingPassword}
                  autoComplete="new-password"
                  minLength={PASSWORD_VALIDATION.MIN_LENGTH}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={styles.eyeButton}
                  aria-label={showPassword ? "Ocultar" : "Mostrar"}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <div className={styles.passwordGroup}>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirmá tu contraseña"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setConfirmPasswordError("");
                  }}
                  className={styles.input}
                  disabled={isSubmittingPassword}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className={styles.eyeButton}
                  aria-label={showConfirmPassword ? "Ocultar" : "Mostrar"}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {(passwordError || confirmPasswordError) && (
                <p className={styles.error}>{passwordError || confirmPasswordError}</p>
              )}
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  className={styles.checkbox}
                />
                <span>
                  Acepto los{" "}
                  <a href={PDF} target="_blank" rel="noopener noreferrer" className={styles.link}>
                    términos y condiciones
                  </a>
                </span>
              </label>
              <button
                type="submit"
                className={styles.primaryButton}
                disabled={
                  isSubmittingPassword ||
                  !password ||
                  !confirmPassword ||
                  !acceptTerms ||
                  !!passwordError ||
                  !!confirmPasswordError
                }
              >
                {isSubmittingPassword ? "Creando cuenta..." : "Crear cuenta"}
              </button>
            </form>
          )}
      </div>
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <ModalOverlay isOpen={isOpen} onClose={handleClose}>
      {content}
    </ModalOverlay>
  );
};

export default JobboardRegistrationFlow;
